/**
 * LLM 调用统一解析：把「上游文本 + 图片资产 + 输入框 @图N 引用」解成一次 LLM 请求参数。
 * 节点按钮（LLMNode）与链路执行（llmGenerator）共用，避免两处口径分叉。
 *
 * 规则（与生图 / 生视频 @引用同口径）：
 *  - 上游文本（text / llm 输出）拼接为 upstreamText；
 *  - 上游图片（含开启透传的上游 LLM 展开）为默认反推图集；
 *  - 输入框含 @图N 标记时切换为精确注入：只用标记引用的图片，标记替换为
 *    「参考图」后并入指令；失效引用计入 warnings 由调用方 toast；
 *  - 解析后无图 = process 文本模式，有图 = vision 反推模式。
 */
import type { CanvasEdge, CanvasNode } from '../types';
import { resolveAtReferences } from '../atReference';
import {
  aggregateUpstreamReferenceImages,
  aggregateUpstreamText,
  collectUpstreamImageAssetGroups,
} from './upstream';

export interface LlmInvocation {
  /** 上游文本（text / llm 输出拼接） */
  upstreamText: string;
  /** 本次实际送入 vision 的图片；为空则走 process 文本模式 */
  images: string[];
  /** 输入框解析后的文本（@图N 已替换为「参考图」） */
  resolvedInput: string;
  /** vision 模式下的附加指令（上游文本 + 输入） */
  instruction?: string;
  /** 是否 vision（反推）模式 */
  isVision: boolean;
  /** 是否存在任何可执行输入（上游文本 / 图片 / 输入框） */
  hasInput: boolean;
  /** 失效 @引用提示（调用方 toast） */
  warnings: string[];
}

export function resolveLlmInvocation(
  targetNodeId: string,
  nodes: CanvasNode[],
  edges: CanvasEdge[],
  rawInput: string
): LlmInvocation {
  const input = rawInput.trim();
  const upstreamText = aggregateUpstreamText(targetNodeId, nodes, edges);

  const knownRefNos = new Set(
    nodes
      .filter((n) => n.type === 'image' || n.type === 'image_group')
      .map((n) => (n.data as Record<string, unknown>).refNo)
      .filter((v): v is number => typeof v === 'number')
  );
  const atResult = resolveAtReferences(
    input,
    collectUpstreamImageAssetGroups(targetNodeId, nodes, edges),
    knownRefNos
  );

  const resolvedInput = atResult ? atResult.prompt.trim() : input;
  // 含标记时精确注入标记引用的图片；无标记时维持上游全量自动聚合
  const images = atResult
    ? atResult.referenceImages
    : aggregateUpstreamReferenceImages(targetNodeId, nodes, edges);

  const isVision = images.length > 0;
  const instruction = [upstreamText.trim(), resolvedInput].filter(Boolean).join('\n') || undefined;

  return {
    upstreamText,
    images,
    resolvedInput,
    instruction,
    isVision,
    hasInput: Boolean(upstreamText || images.length > 0 || input),
    warnings: atResult?.warnings ?? [],
  };
}
